// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { LocaleProvider } from './contexts/LocaleContext';
import ProtectedRoute from './components/ProtectedRoute';
import { isFirebaseConfigured, getFirebaseStatus } from './firebase/config';
import {
  initializeAnalytics,
  trackPageView,
  trackSessionStart,
  setAnalyticsUserId
} from './utils/analytics';

// Import components
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import Dashboard from './components/dashboard/Dashboard';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Blog from './components/blog/Blog';
import BlogPost from './components/blog/BlogPost';

// Component for tracking route changes
const RouteTracker = () => {
  const location = useLocation();
  const { useAuth } = require('./contexts/AuthContext');
  const { currentUser } = useAuth();

  // Track page views on route changes
  useEffect(() => {
    const pageTitle = getPageTitle(location.pathname);
    trackPageView(pageTitle, location.pathname + location.search);
  }, [location]);

  // Set user ID when user logs in
  useEffect(() => {
    if (currentUser) {
      setAnalyticsUserId(currentUser.uid);
    }
  }, [currentUser]);

  return null;
};

// Helper function to get page titles
const getPageTitle = (pathname) => {
  const titles = {
    '/': 'PDF Statement Converter - Home',
    '/us': 'Convert US Bank Statements to QBO, CSV, OFX - PDF Statement Converter',
    '/ca': 'Convert Canadian Bank Statements to QBO, CSV, OFX - PDF Statement Converter',
    '/uk': 'Convert UK Bank Statements to QBO, CSV, OFX - PDF Statement Converter',
    '/br': 'Converter Extrato Bancário PDF para CSV, OFX, QBO - PDF Statement Converter',
    '/ar': 'Convertir Extractos Bancarios PDF a CSV, OFX, QBO - PDF Statement Converter',
    '/ae': 'Convert UAE Bank Statements to QBO, CSV, OFX - PDF Statement Converter',
    '/za': 'Convert SA Bank Statements to QBO, CSV, OFX - PDF Statement Converter',
    '/login': 'Login - PDF Statement Converter',
    '/register': 'Register - PDF Statement Converter',
    '/forgot-password': 'Reset Password - PDF Statement Converter',
    '/dashboard': 'Dashboard - PDF Statement Converter',
    '/blog': 'Blog - PDF Statement Converter',
    '/us/blog': 'US Blog - PDF Statement Converter',
    '/ca/blog': 'Canadian Blog - PDF Statement Converter',
    '/uk/blog': 'UK Blog - PDF Statement Converter',
    '/br/blog': 'Brazil Blog - PDF Statement Converter',
    '/ar/blog': 'Argentina Blog - PDF Statement Converter',
    '/ae/blog': 'UAE Blog - PDF Statement Converter',
    '/za/blog': 'South African Blog - PDF Statement Converter',
    '/test': 'Test Page - PDF Statement Converter'
  };

  // Handle dynamic blog post routes (country-prefixed and universal)
  if (pathname.match(/^\/[a-z]{2}\/blog\/.+/) || pathname.startsWith('/blog/')) {
    return 'Blog Post - PDF Statement Converter';
  }

  return titles[pathname] || 'PDF Statement Converter';
};

// Create a separate component that can use useAuth
const AppContent = () => {
  const { useAuth } = require('./contexts/AuthContext');
  const { currentUser, userCredits } = useAuth();

  useEffect(() => {
    console.log('=== AUTH CONTEXT TEST ===');
    console.log('Current User:', currentUser);
    console.log('User Credits:', userCredits);
    console.log('Is Logged In:', !!currentUser);
  }, [currentUser, userCredits]);

  return (
    <div className="min-h-screen bg-gray-50">
      <RouteTracker />
      <Routes>
        <Route path="/us" element={<LandingPage forceCountry="US" />} />
        <Route path="/ca" element={<LandingPage forceCountry="CA" />} />
        <Route path="/uk" element={<LandingPage forceCountry="GB" />} />
        <Route path="/br" element={<LandingPage forceCountry="BR" />} />
        <Route path="/ar" element={<LandingPage forceCountry="AR" />} />
        <Route path="/ae" element={<LandingPage forceCountry="AE" />} />
        <Route path="/za" element={<LandingPage forceCountry="ZA" />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {/* Dashboard is now accessible to both authenticated and anonymous users */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        {/* Country blog routes */}
        <Route path="/us/blog" element={<Blog forceCountry="US" />} />
        <Route path="/us/blog/:slug" element={<BlogPost forceCountry="US" />} />
        <Route path="/ca/blog" element={<Blog forceCountry="CA" />} />
        <Route path="/ca/blog/:slug" element={<BlogPost forceCountry="CA" />} />
        <Route path="/uk/blog" element={<Blog forceCountry="GB" />} />
        <Route path="/uk/blog/:slug" element={<BlogPost forceCountry="GB" />} />
        <Route path="/br/blog" element={<Blog forceCountry="BR" />} />
        <Route path="/br/blog/:slug" element={<BlogPost forceCountry="BR" />} />
        <Route path="/ar/blog" element={<Blog forceCountry="AR" />} />
        <Route path="/ar/blog/:slug" element={<BlogPost forceCountry="AR" />} />
        <Route path="/ae/blog" element={<Blog forceCountry="AE" />} />
        <Route path="/ae/blog/:slug" element={<BlogPost forceCountry="AE" />} />
        <Route path="/za/blog" element={<Blog forceCountry="ZA" />} />
        <Route path="/za/blog/:slug" element={<BlogPost forceCountry="ZA" />} />
        {/* Universal blog routes */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        {/* Test route for debugging */}
        <Route
          path="/test"
          element={
            <div className="container mx-auto p-6">
              <h1 className="text-2xl font-bold mb-6">Day 5 Test</h1>
              <Dashboard />
            </div>
          }
        />
      </Routes>
    </div>
  );
};

function App() {
  // Initialize Firebase and Analytics
  useEffect(() => {
    console.log('=== FIREBASE STATUS ===');
    console.log('Configured:', isFirebaseConfigured());
    console.log('Status:', getFirebaseStatus());
    
    // Initialize Analytics
    const analytics = initializeAnalytics();
    if (analytics) {
      console.log('✅ Analytics initialized successfully');
      
      // Track app session start
      trackSessionStart({
        app_version: process.env.REACT_APP_VERSION || '1.0.0',
        environment: process.env.NODE_ENV
      });
    } else {
      console.warn('⚠️ Analytics initialization failed');
    }
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <LocaleProvider>
            <AppContent />
          </LocaleProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;